import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/lib/supabase';
import FeexPaySettingsPanel from './FeexPaySettingsPanel';
import { normalizeFeexPayApiUrl } from '@/lib/feexpay';

interface PaymentProvider {
  id: number;
  provider: string;
  label: string;
  description: string | null;
  enabled: boolean;
  mode: string;
  public_key: string | null;
  config: Record<string, unknown> | null;
  secret_configured: boolean;
  sort_order: number;
}

interface ConfigField {
  key: string;
  label: string;
  placeholder?: string;
  help?: string;
  /** Champ stocké dans la colonne public_key (sinon dans config JSONB) */
  primary?: boolean;
}

/** Champs de configuration NON secrets par fournisseur.
 *  Les clés secrètes (secret key, store key, api key…) ne sont JAMAIS
 *  stockées ici : elles doivent être placées dans les secrets du backend. */
const PROVIDER_FIELDS: Record<string, ConfigField[]> = {
  stripe: [
    { key: 'public_key', label: 'Clé publique (publishable)', placeholder: 'pk_live_...', primary: true, help: 'La clé secrète se configure via l’intégration Stripe.' },
  ],
  paypal: [
    { key: 'public_key', label: 'Client ID', placeholder: 'AX...', primary: true, help: 'Le Secret se configure via l’intégration PayPal.' },
  ],
  cmi: [
    { key: 'public_key', label: 'Identifiant marchand (Merchant ID)', placeholder: 'Votre Merchant ID', primary: true },
    { key: 'gateway_url', label: 'URL de la passerelle CMI', placeholder: 'https://payment.cmi.co.ma/...', help: 'URL fournie par votre banque.' },
  ],
  wave: [
    { key: 'public_key', label: 'Wallet ID Wave', placeholder: 'wal_...', primary: true },
  ],
  flutterwave: [
    { key: 'public_key', label: 'Clé publique (FLWPUBK)', placeholder: 'FLWPUBK-...', primary: true, help: 'La clé secrète (FLWSECK) va dans les secrets du backend.' },
  ],
  feexpay: [
    { key: 'public_key', label: 'Shop ID FeexPay', placeholder: 'Ayg9lkjkhurIvNp', primary: true, help: 'Identifiant de votre boutique, visible dans « Boutique » de votre tableau de bord FeexPay. Copiez-le tel quel : aucune longueur n’est imposée. Ce n’est pas la clé API (fp_…).' },
    { key: 'api_key', label: 'Clé API FeexPay', placeholder: 'fp_...', help: 'Envoyée en Authorization: Bearer par le serveur. Jamais exposée au navigateur.' },
    { key: 'api_url', label: 'URL de l’API (v2)', placeholder: 'https://api-v2.feexpay.me', help: 'Hôte unique de l’API FeexPay : https://api-v2.feexpay.me. L’ancien hôte « sandbox » ne fonctionne plus.' },
    { key: 'callback_url', label: 'URL de callback (webhook)', placeholder: 'https://.../functions/v1/feexpay-callback', help: 'À recopier dans votre tableau de bord FeexPay.' },
  ],
};

export default function PaymentProvidersSettings() {
  const [providers, setProviders] = useState<PaymentProvider[]>([]);
  const [loading, setLoading] = useState(true);
  const [savingId, setSavingId] = useState<number | null>(null);
  const [savedId, setSavedId] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);

  const fetchProviders = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const { data, error: fetchError } = await supabase
        .from('payment_providers')
        .select('*')
        .order('sort_order');
      if (fetchError) throw fetchError;
      setProviders((data || []) as PaymentProvider[]);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Erreur de chargement');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchProviders();
  }, [fetchProviders]);

  const updateProvider = (id: number, patch: Partial<PaymentProvider>) => {
    setProviders((prev) => prev.map((p) => (p.id === id ? { ...p, ...patch } : p)));
  };

  const updateConfigField = (id: number, fieldKey: string, value: string) => {
    setProviders((prev) => prev.map((p) => {
      if (p.id !== id) return p;
      const config = { ...(p.config || {}) };
      config[fieldKey] = value;
      return { ...p, config };
    }));
  };

  const patchConfig = (id: number, patch: Record<string, unknown>) => {
    setProviders((prev) => prev.map((p) => (p.id === id ? { ...p, config: { ...(p.config || {}), ...patch } } : p)));
  };

  const handleSave = async (provider: PaymentProvider) => {
    setSavingId(provider.id);
    setError(null);
    try {
      // FeexPay n'a qu'un seul hôte d'API : on corrige toute URL « sandbox »
      // enregistrée (l'ancien hôte ne résout plus en DNS).
      const nextConfig = { ...(provider.config || {}) };
      if (provider.provider === 'feexpay') {
        nextConfig.api_url = normalizeFeexPayApiUrl(String(nextConfig.api_url ?? ''));
      }
      // FeexPay fonctionne uniquement en production (LIVE) : on force le mode
      // pour que le SDK React (`mode="LIVE"`) et le serveur restent alignés.
      const nextMode = provider.provider === 'feexpay' ? 'live' : provider.mode;
      const { error: saveError } = await supabase
        .from('payment_providers')
        .update({
          enabled: provider.enabled,
          mode: nextMode,
          public_key: provider.public_key || '',
          config: nextConfig,
          updated_at: new Date().toISOString(),
        })
        .eq('id', provider.id);
      if (saveError) throw saveError;
      setSavedId(provider.id);
      setTimeout(() => setSavedId(null), 2000);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Erreur lors de la sauvegarde');
    } finally {
      setSavingId(null);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <i className="ri-loader-4-line animate-spin text-2xl text-primary-500"></i>
      </div>
    );
  }

  const enabledCount = providers.filter((p) => p.enabled).length;

  return (
    <section className="bg-background-50 border border-background-200/70 rounded-lg p-5">
      <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
        <div className="flex items-center gap-2">
          <div className="w-9 h-9 rounded-lg bg-accent-50 flex items-center justify-center flex-shrink-0">
            <i className="ri-bank-card-line text-accent-600"></i>
          </div>
          <div>
            <h3 className="text-sm font-bold font-heading text-foreground-950">Moyens de paiement d&apos;abonnement</h3>
            <p className="text-xs text-foreground-500">
              Activez et paramétrez les moyens de paiement proposés pour l&apos;abonnement Zifek.
            </p>
          </div>
        </div>
        <span className="text-xs px-3 py-1 rounded-full bg-background-100 text-foreground-500 font-medium whitespace-nowrap">
          {enabledCount}/{providers.length} activés
        </span>
      </div>

      {error && (
        <div className="mb-4 px-4 py-3 bg-red-50 text-red-600 rounded-lg text-sm flex items-center gap-2">
          <i className="ri-error-warning-line"></i>
          {error}
        </div>
      )}

      <div className="space-y-3">
        {providers.map((provider) => {
          const fields = PROVIDER_FIELDS[provider.provider] || [];
          const isSaving = savingId === provider.id;
          return (
            <div
              key={provider.id}
              className={`border rounded-lg p-4 transition-colors ${
                provider.enabled ? 'border-accent-200/70 bg-accent-50/30' : 'border-background-200/70 bg-background-50'
              }`}
            >
              <div className="flex items-start justify-between gap-3 flex-wrap">
                <div className="min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-sm font-semibold text-foreground-900">{provider.label}</span>
                    {provider.enabled ? (
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium bg-accent-100 text-accent-700">
                        <span className="w-1.5 h-1.5 rounded-full bg-accent-500"></span>Actif
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium bg-background-100 text-foreground-500">
                        Inactif
                      </span>
                    )}
                    <span className="text-[10px] px-2 py-0.5 rounded-full bg-background-100 text-foreground-500">
                      {provider.secret_configured ? 'Clé secrète configurée' : 'Clé secrète à configurer'}
                    </span>
                  </div>
                  {provider.description && (
                    <p className="text-xs text-foreground-500 mt-1">{provider.description}</p>
                  )}
                </div>

                {/* Toggle */}
                <button
                  type="button"
                  onClick={() => updateProvider(provider.id, { enabled: !provider.enabled })}
                  className={`relative inline-flex h-7 w-12 items-center rounded-full transition-colors cursor-pointer flex-shrink-0 ${
                    provider.enabled ? 'bg-accent-500' : 'bg-background-200'
                  }`}
                  aria-label={provider.enabled ? 'Désactiver' : 'Activer'}
                >
                  <span
                    className={`inline-block h-5 w-5 transform rounded-full bg-white transition-transform ${
                      provider.enabled ? 'translate-x-6' : 'translate-x-1'
                    }`}
                  />
                </button>
              </div>

              {/* Champs de configuration */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-4">
                {/* Mode : FeexPay n'expose plus qu'un hôte live (plus de sandbox) */}
                {provider.provider === 'feexpay' ? (
                  <div>
                    <label className="block text-[11px] font-semibold text-foreground-600 mb-1">Mode</label>
                    <div className="inline-flex items-center gap-1.5 h-10 px-3 rounded-lg border border-accent-200/70 bg-accent-50 text-[11px] font-semibold text-accent-700">
                      <span className="w-1.5 h-1.5 rounded-full bg-accent-500"></span>
                      LIVE — production
                    </div>
                    <p className="text-[10px] text-foreground-400 mt-1">La modale FeexPay est toujours ouverte en mode LIVE.</p>
                  </div>
                ) : (
                  <div>
                    <label className="block text-[11px] font-semibold text-foreground-600 mb-1">Mode</label>
                    <div className="flex items-center gap-0.5 bg-background-100 rounded-full p-0.5 w-fit">
                      {(['sandbox', 'live'] as const).map((m) => (
                        <button
                          key={m}
                          type="button"
                          onClick={() => updateProvider(provider.id, { mode: m })}
                          className={`px-3 py-1.5 rounded-full text-[11px] font-medium whitespace-nowrap transition-colors cursor-pointer ${
                            provider.mode === m ? 'bg-background-50 text-foreground-900 shadow-sm' : 'text-foreground-500 hover:text-foreground-700'
                          }`}
                        >
                          {m === 'sandbox' ? 'Test' : 'Production'}
                        </button>
                      ))}
                    </div>
                  </div>
                )}
                {fields.map((field) => {
                  const value = field.primary
                    ? (provider.public_key || '')
                    : String(provider.config?.[field.key] ?? '');
                  return (
                    <div key={field.key}>
                      <label className="block text-[11px] font-semibold text-foreground-600 mb-1">{field.label}</label>
                      <input
                        type="text"
                        value={value}
                        onChange={(e) => {
                          if (field.primary) updateProvider(provider.id, { public_key: e.target.value });
                          else updateConfigField(provider.id, field.key, e.target.value);
                        }}
                        placeholder={field.placeholder}
                        className="w-full h-10 px-3 rounded-lg border border-background-200/70 bg-background-50 text-xs text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-300 font-mono"
                      />
                      {field.help && <p className="text-[10px] text-foreground-400 mt-1">{field.help}</p>}
                    </div>
                  );
                })}
              </div>

              {provider.provider === 'feexpay' && String(provider.public_key || '').trim() === '' && (
                <div className="mt-3 flex items-start gap-2.5 px-3 py-2.5 rounded-lg border border-amber-200 bg-amber-50">
                  <i className="ri-error-warning-line text-amber-600 mt-0.5"></i>
                  <div className="min-w-0">
                    <p className="text-[11px] font-semibold text-foreground-900">Shop ID manquant</p>
                    <p className="text-[10px] text-foreground-600 mt-0.5">
                      Renseignez l’identifiant de votre boutique FeexPay (onglet « Boutique » du tableau de bord).
                      Aucune longueur n’est imposée : copiez la valeur telle qu’elle apparaît.
                    </p>
                  </div>
                </div>
              )}

              {provider.provider === 'feexpay' && (
                <FeexPaySettingsPanel
                  config={provider.config || {}}
                  onPatch={(patch) => patchConfig(provider.id, patch)}
                />
              )}

              <div className="flex items-center justify-between gap-3 mt-4 flex-wrap">
                <p className="text-[10px] text-foreground-400 flex items-center gap-1.5 max-w-md">
                  <i className="ri-shield-check-line text-accent-500"></i>
                  Les clés secrètes ne sont jamais stockées ici : placez-les dans les secrets sécurisés du backend.
                </p>
                <button
                  type="button"
                  onClick={() => handleSave(provider)}
                  disabled={isSaving}
                  className="flex items-center gap-2 px-4 py-2 rounded-full text-xs font-semibold whitespace-nowrap cursor-pointer bg-foreground-950 text-background-50 hover:bg-foreground-800 transition-colors disabled:opacity-50"
                >
                  {isSaving ? (
                    <><i className="ri-loader-4-line animate-spin"></i>Enregistrement...</>
                  ) : savedId === provider.id ? (
                    <><i className="ri-check-line"></i>Enregistré</>
                  ) : (
                    <><i className="ri-save-line"></i>Enregistrer</>
                  )}
                </button>
              </div>
            </div>
          );
        })}

        {providers.length === 0 && (
          <p className="text-sm text-foreground-500 text-center py-8">Aucun fournisseur de paiement disponible.</p>
        )}
      </div>
    </section>
  );
}